import { useState } from 'react'
import { useJobs, useSaveJob, useDeleteJob, type Job } from '@/hooks/useJobs'
import { Plus, Edit2, Trash2 } from 'lucide-react'

export default function Jobs() {
    const { jobs, loading } = useJobs()
    const saveJob = useSaveJob()
    const deleteJob = useDeleteJob()
    const [editing, setEditing] = useState<Partial<Job> | null>(null)

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!editing?.title || !editing?.slug) return

        await saveJob.mutateAsync({
            ...editing,
            title: editing.title,
            slug: editing.slug.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
            type: editing.type || 'Job',
            isActive: editing.isActive ?? true,
        } as any)
        setEditing(null)
    }

    return (
        <div>
            <div className="page-header" style={{ marginBottom: 24 }}>
                <div>
                    <h1 className="page-title">Jobs & Internships</h1>
                    <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Manage your active listings</div>
                </div>
                <button className="btn btn-primary" onClick={() => setEditing({ type: 'Job', isActive: true, requirements: [] })}>
                    <Plus size={16} /> New Listing
                </button>
            </div>

            {editing && (
                <div className="card" style={{ marginBottom: 24, padding: 24, border: '1px solid var(--accent-glow)' }}>
                    <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>{editing.id ? 'Edit Job' : 'Create Job'}</h2>
                    <form onSubmit={handleSave} style={{ display: 'grid', gap: 16 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <div>
                                <label className="label">Title</label>
                                <input required className="input" value={editing.title || ''} onChange={e => setEditing(p => ({ ...p, title: e.target.value, slug: !p?.id && !p?.slug ? e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-') : p?.slug }))} />
                            </div>
                            <div>
                                <label className="label">Slug (URL ID)</label>
                                <input required className="input" value={editing.slug || ''} onChange={e => setEditing(p => ({ ...p, slug: e.target.value }))} disabled={!!editing.id} />
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <div>
                                <label className="label">Type</label>
                                <select className="input" value={editing.type} onChange={e => setEditing(p => ({ ...p, type: e.target.value as 'Job' | 'Internship' }))}>
                                    <option value="Job">Job</option>
                                    <option value="Internship">Internship</option>
                                </select>
                            </div>
                            <div>
                                <label className="label">Department / Location</label>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <input className="input" placeholder="Engineering" value={editing.department || ''} onChange={e => setEditing(p => ({ ...p, department: e.target.value }))} />
                                    <input className="input" placeholder="Remote" value={editing.location || ''} onChange={e => setEditing(p => ({ ...p, location: e.target.value }))} />
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="label">Description</label>
                            <textarea className="input" rows={4} value={editing.description || ''} onChange={e => setEditing(p => ({ ...p, description: e.target.value }))} />
                        </div>

                        <div>
                            <label className="label">Requirements (one per line)</label>
                            <textarea className="input" rows={4} value={editing.requirements?.join('\n') || ''} onChange={e => setEditing(p => ({ ...p, requirements: e.target.value.split('\n').filter(Boolean) }))} />
                        </div>

                        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
                            <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
                            <button type="submit" className="btn btn-primary" disabled={saveJob.isPending}>{saveJob.isPending ? 'Saving...' : 'Save Job'}</button>
                        </div>
                    </form>
                </div>
            )}

            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {loading ? (
                    <div style={{ padding: 60, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
                ) : jobs.length === 0 ? (
                    <div className="empty-state">No jobs found. Create one to get started.</div>
                ) : (
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Title</th>
                                <th>Type</th>
                                <th>Department</th>
                                <th>Status</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {jobs.map(job => (
                                <tr key={job.id}>
                                    <td>
                                        <div style={{ fontWeight: 600 }}>{job.title}</div>
                                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{job.slug}</div>
                                    </td>
                                    <td>{job.type}</td>
                                    <td>{job.department} <span style={{ color: 'var(--text-muted)' }}>{job.location ? `• ${job.location}` : ''}</span></td>
                                    <td><span className={`badge ${job.isActive ? 'badge-shortlisted' : 'badge-rejected'}`}>{job.isActive ? 'Active' : 'Closed'}</span></td>
                                    <td style={{ textAlign: 'right' }}>
                                        <button className="btn btn-ghost btn-sm" onClick={() => setEditing(job)} style={{ marginRight: 8 }}><Edit2 size={14} /></button>
                                        <button className="btn btn-danger btn-sm" onClick={() => { if (confirm('Are you sure?')) deleteJob.mutate(job.id) }}><Trash2 size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    )
}
